import {
    Box, Flex, Text, Button, Divider,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useDisclosure, HStack, Tag, TagLabel,
} from "@chakra-ui/react";

import { OrderDto, SubscriptionDto, UserDto } from "doxecure-commons/doxecureClientTypes";
import { ProductStructure } from "doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { $timeBetweenDates } from "foundation-ts/date";
import { TSDate } from "foundation-ts/tsdate";
import { Nullable } from "foundation-ts/types";

import { G1Button } from "../G1Button";

import { formatAmount } from "./helpers";
import { Card, ProgressBar } from "./UI";

interface PlanCardProps {
    connectedUser?: Nullable<UserDto>;
    subscription: Nullable<SubscriptionDto>;
    extraUsers: number;
    isLoadingStartingNow: boolean;
    isLoadingBuyExtraPack: boolean;
    isLoadingUpgradeToUnlimited: boolean;
    pendingOrder?: Nullable<OrderDto>;
    productUnlimited?: ProductStructure;
    productCollab?: ProductStructure;
    productPack?: ProductStructure;
    onBuyExtraPack: () => void;
    onCancelTerminationRequest: () => void;
    onChangeToUnlimitedPlan: () => void;
    onStartNow: (unlimited: boolean) => void;
    onTerminate: () => void;
}

export function PlanCard(props: PlanCardProps) {
    const { connectedUser, subscription, extraUsers, pendingOrder, isLoadingStartingNow, isLoadingBuyExtraPack, isLoadingUpgradeToUnlimited, productUnlimited, productCollab, productPack, onBuyExtraPack, onCancelTerminationRequest, onChangeToUnlimitedPlan, onStartNow, onTerminate } = props;
    const changeToUnlimitedPlanModal    = useDisclosure();
    const terminateModal                = useDisclosure();
    const startNowModal                 = useDisclosure();
    const extracPackModal               = useDisclosure();
    const cancelTerminationModal        = useDisclosure();

    const isTrialPeriod             = $ok(subscription?.trialPeriod);
    const isCancelationRequested    = $ok(subscription?.requestCancelSubscription);
    const isEnableBuyCreditPack     = $ok(subscription) && !isTrialPeriod && !subscription?.unlimited && (subscription?.signatureCount.packMax === 0 || (subscription!.signatureCount.packMax - subscription!.signatureCount.packConsumed) <= MIN_AVAILABLE_SIGNATURE_PACK);
    const isCreditPackInfoToBeShown = $ok(subscription) && !isTrialPeriod && !subscription?.unlimited && (subscription!.signatureCount.packMax - subscription!.signatureCount.packConsumed) > MIN_AVAILABLE_SIGNATURE_PACK;
    const hasPendingOrder           = $ok(pendingOrder);
    const sigPct                    = $ok(subscription) ? (subscription!.signatureCount.subscriptionConsumed / subscription!.signatureCount.subscriptionMax) * 100 : 0;
    const trialLeft                 = isTrialPeriod ? remainingTrialDays(TSDate.zulu().dateWithoutTime(), subscription!.trialPeriod!.end) : -1;
    const extraUsersPrice           = $ok(subscription) ? extraUsers * productCollab!.price * (1 + subscription!.vatRate / 100) : 0;
    const priceTTC                  = $ok(subscription) ? extraUsersPrice + (subscription!.priceHT * (1 + subscription!.vatRate / 100)) : 0;
    const unlimitedPriceTTC         = $ok(subscription) ? extraUsersPrice + (productUnlimited!.price * (1 + subscription!.vatRate / 100)) : 0;
    const packPriceTTC              = $ok(subscription) ? productPack!.price * (1 + subscription!.vatRate / 100) : 0;
    const maxAvailableSignatures    = $ok(subscription) ? subscription!.signatureCount.subscriptionMax + subscription!.signatureCount.packMax : 0;

    function handleChangeToUnlimitedPlan(): void {
        onChangeToUnlimitedPlan();
        changeToUnlimitedPlanModal.onClose();
    }

    function handleTerminate(): void {
        onTerminate();
        terminateModal.onClose();
    }

    function handleCancelTerminationRequest(): void {
        onCancelTerminationRequest();
        cancelTerminationModal.onClose();
    }

    function handleStartNow(unlimited: boolean): void {
        onStartNow(unlimited);
        startNowModal.onClose();
    }

    function handleBuyExtraPack(): void {
        onBuyExtraPack();
        extracPackModal.onClose();
    }
    function openChangePlan(): void {
        changeToUnlimitedPlanModal.onOpen();
    }

    const actions = [
        {
            show: isTrialPeriod && !isCancelationRequested,
            label: "Commencer maintenant",
            style: {
                color: "white",
                hoverBg: "brand.600",
                bg: "brand.500",
            },
            isLoading: isLoadingStartingNow,
            onClick: startNowModal.onOpen
        }, 
        {
            show: !isTrialPeriod && !subscription?.unlimited && !isCancelationRequested,
            label: "Passer à l'illimité",
            style: {
                color: "white",
                hoverBg: "brand.600",
                bg: "brand.500",
            },
            isDisabled: hasPendingOrder,
            isLoading: isLoadingUpgradeToUnlimited,
            onClick: changeToUnlimitedPlanModal.onOpen
        },
        {
            show: !isTrialPeriod && !!subscription?.unlimited && !isCancelationRequested,
            label: "Revenir à l'abonnement standard",
            style: {
                color: "white",
                hoverBg: "brand.600",
                bg: "brand.500",
            },
            onClick: openChangePlan
        },
        {
            show: isCancelationRequested,
            label: "Annuler la résiliation",
            style: {
                color: "red.400",
                hoverBg: "red.50",
                variant: "ghost"
            },
            onClick: cancelTerminationModal.onOpen
        },
        {
            show: !isCancelationRequested,
            label: "Résilier",
            style: {
                color: "red.400",
                hoverBg: "red.50",
                variant: "ghost"
            },
            isDisabled: hasPendingOrder,
            onClick: terminateModal.onOpen
        }
    ];

    const tags = [
        {
            show: isTrialPeriod,
            label: `Essai — ${trialLeft > 1 ? `${trialLeft}j restants` : 'dernier jour'}`,
            style: {
                bg: "orange.50",
                borderColor: "orange.200",
                color: "orange.600"
            },
        },
        {
            show: isCancelationRequested,
            label: "Résiliation en cours",
            style: {
                bg: "red.50",
                borderColor: "red.200",
                color: "red.600"
            }
        }
    ]

    return (
        <>
            <Card gridColumn={{ lg: "span 2" }}>
                <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
                    <Box>
                        <HStack spacing={3} gap={2} mb={1} flexWrap="wrap">
                            <Text fontFamily="heading" fontSize="xl" color="gray.900">
                                {subscription?.name}
                            </Text>
                            { tags
                                .filter(({ show }) => show)
                                .map(({ label, style }, index) => (
                                    <Tag key={index} size="sm" borderRadius="full" bg={style.bg} border="1px solid" borderColor={style.borderColor} px={3} py={1}>
                                        <TagLabel fontSize="xs" color={style.color} fontWeight="600">
                                            {label}
                                        </TagLabel>
                                    </Tag>
                                ))
                            }
                        </HStack>
                        <Text fontSize="sm" color="gray.400">
                            {formatAmount(subscription?.priceHT)} HT / an · 1 mois d'essai ·{" "}
                            {!!subscription?.unlimited ? "Signatures illimitées" : `${subscription?.signaturesPerYear} signatures / an`}
                        </Text>
                        { isCancelationRequested && (
                            <Text fontSize="sm" color="red.400">
                                Résiliation demandée le {subscription?.requestCancelSubscription?.requestDate?.toString("%d %B %Y")} par {connectedUser?.apid === subscription?.requestCancelSubscription?.requesterId ? "vous-même" : subscription?.requestCancelSubscription?.requesterName} ·{" "}
                                {`Votre abonnement prendra donc fin le ${subscription?.requestCancelSubscription?.end?.toString("%d %B %Y")}`}
                            </Text> 
                        )}
                        { hasPendingOrder && (
                            <Text fontSize="sm" color="red.400">
                                {connectedUser?.apid === pendingOrder?.requesterInfos?.requesterId ? "Votre demande d'achat" : `La demande effectuée par ${pendingOrder?.requesterInfos?.requesterName}`} a été prise en compte. Un email de confirmation vous sera envoyé dès réception de la validation du réglement.
                            </Text> 
                        )}
                    </Box>
                    <HStack spacing={2} flexWrap="wrap">
                        { actions
                            .filter(({ show }) => show)
                            .map(({ label, style, isDisabled, isLoading, onClick }, index) => (
                                <Button key={index} size="sm" variant={style?.variant ?? "solid"} color={style?.color} bg={style?.bg ?? "transparent"}
                                        _hover={{ bg: style.hoverBg }} isDisabled={isDisabled} isLoading={isLoading} onClick={onClick}>
                                    {label}
                                </Button>
                            ))
                        }
                    </HStack>
                </Flex>

                <Divider my={5} borderColor="gray.100" />

                { !subscription?.unlimited ? (
                    <Box mb={isTrialPeriod ? 0 : 5}>
                        <Flex justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="600" color="gray.700">Signatures consommées / disponibles</Text>
                            <Text fontSize="sm" color={sigPct > 80 ? "red.500" : "gray.500"}>
                                <Text as="span" fontWeight="700" color="gray.800">{subscription?.signatureCount.subscriptionConsumed}</Text>
                                {" / "}{maxAvailableSignatures}
                            </Text>
                        </Flex>
                        <ProgressBar value={subscription?.signatureCount.subscriptionConsumed ?? 0} max={maxAvailableSignatures} color={!!subscription?.unlimited ? "#0d1a5c": "#3354f4"} />
                        {sigPct > 80 && (
                            <Text fontSize="xs" color="red.400" mt={1}>Vous approchez de votre quota annuel.</Text>
                        )}
                    </Box>
                ) : (
                    <Flex align="center" gap={2} mb={0}>
                        <Box w="8px" h="8px" borderRadius="full" bg="green.400" flexShrink={0} />
                        <Text fontSize="sm" color="gray.600">
                            <Text as="span" fontWeight="600" color="gray.800">{subscription?.signatureCount.subscriptionConsumed}</Text>
                            {" "}signatures effectuées cette année · quota illimité
                        </Text>
                    </Flex>
                )}

                { isEnableBuyCreditPack && (
                    <Box bg="brand.50" borderRadius="12px" p={4} border="1px dashed" borderColor="brand.100">
                        <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                            <Box>
                                <Text fontSize="sm" fontWeight="600" color="brand.700">Besoin de signatures supplémentaires ?</Text>
                                <Text fontSize="xs" color="brand.500" mt={0.5}>Achetez un pack de {productPack?.signatures} crédits</Text>
                            </Box>
                            <Button size="sm" bg="brand.500" color="white" _hover={{ bg: "brand.600" }} flexShrink={0} isLoading={isLoadingBuyExtraPack} onClick={extracPackModal.onOpen}>
                                Acheter un pack
                            </Button>
                        </Flex>
                    </Box>
                )}
                { isCreditPackInfoToBeShown && (
                    <Box bg="brand.50" borderRadius="12px" p={4} border="1px dashed" borderColor="brand.100">
                        <Text fontSize="sm" fontWeight="600" color="brand.700">⚠ Vous aurez la possibilité d'acheter un nouveau pack de signatures lorsqu'il vous restera moins de 20 signatures disponibles.</Text>
                    </Box>
                )}
            </Card>

            <Modal isOpen={changeToUnlimitedPlanModal.isOpen} onClose={changeToUnlimitedPlanModal.onClose} size="lg" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Passer à l'illimité</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            Êtes-vous sûr de vouloir convertir votre abonnement en illimité ?
                        </Text>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                            Un supplément de <strong>{subscription?.infosToUnlimitedUpgrade?.price}€</strong> vous sera prélevé correspondant au {subscription?.infosToUnlimitedUpgrade?.days} jours restants{" "}
                            avant son renouvellement.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={changeToUnlimitedPlanModal.onClose} />
                        <G1Button component="form" variant="important" title="Confirmer" flex={1} onClick={handleChangeToUnlimitedPlan} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={terminateModal.isOpen} onClose={terminateModal.onClose} size="md" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading" color="red.500">Résilier l'abonnement</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            Êtes-vous sûr de vouloir résilier votre <strong>{subscription?.name}</strong> ?
                        </Text>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                            Vous conservez l'accès jusqu'à la fin de votre période en cours.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Conserver" flex={1} onClick={terminateModal.onClose} />
                        <G1Button component="form" variant="delete" title="Résilier" flex={1} onClick={handleTerminate} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={cancelTerminationModal.isOpen} onClose={cancelTerminationModal.onClose} size="md" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading" color="red.500">Annuler la résiliation</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">Êtes-vous sûr de vouloir annuler la résiliation ?</Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Non" flex={1} onClick={terminateModal.onClose} />
                        <G1Button component="form" variant="delete" title="Oui" flex={1} onClick={handleCancelTerminationRequest} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={startNowModal.isOpen} onClose={startNowModal.onClose} size="lg" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Commencer maintenant</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            En démarrant votre <strong>{subscription?.name}</strong> dès maintenant, votre période d'essai prendra fin dès réception du réglement.
                        </Text>
                        <Text fontSize="sm" color="gray.600" mt={2}>
                            Vous avez la possibilité de passer directement à l'abonnement illimité pour <strong>{formatAmount(unlimitedPriceTTC)} TTC / an</strong>{" "}
                            ou rester sur l'abonnement standard à 60 signatures pour <strong>{formatAmount(priceTTC)} TTC / an</strong>.
                        </Text>
                        { extraUsers > 0 && (
                            <Text fontSize="sm" fontStyle='italic' color="gray.600">
                                Ces prix prennent en compte les {extraUsers} collaborateurs ajoutés pendant la période d'essai.
                            </Text>
                        )}
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={startNowModal.onClose} />
                        <G1Button component="form" variant="alternate" title="Illimité" flex={1} onClick={() => handleStartNow(true)} />
                        <G1Button component="form" variant="normal" title="Standard" flex={1} onClick={() => handleStartNow(false)} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={extracPackModal.isOpen} onClose={extracPackModal.onClose} size="sm" isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Acheter un pack de crédits</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600">
                            Le pack contient {productPack?.signatures} signatures au prix de {productPack?.price}€ HT (soit {formatAmount(packPriceTTC)} TTC).{" "}
                            Ces crédits sont utilisés à partir du moment où votre abonnement est épuisé et restent valides même après le renouvellement de votre abonnement.
                        </Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={extracPackModal.onClose} />
                        <G1Button component="form" variant="normal" title="Acheter" flex={1} onClick={handleBuyExtraPack} />
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

const remainingTrialDays = (a: TSDate, b: TSDate): number => Math.floor(Math.abs($timeBetweenDates(a, b)) / (3600 * 24));
const MIN_AVAILABLE_SIGNATURE_PACK = 20;